import Decimal from 'decimal.js'
import type { AdminSalesSeries, HourlyReceivablePoint } from '../api/admin.api'

const HOUR_MS = 60 * 60 * 1000
export const HOURS_IN_DAY = 24

export type HourBucket = {
  index: number
  startsAt: string
  amount: Decimal
  count: number
}

/** Completa as 24 horas do dia; o backend só devolve horas com lançamento. */
export function hourlyBuckets(dayStart: string, items: readonly HourlyReceivablePoint[] = []) {
  const start = Date.parse(dayStart)
  const buckets: HourBucket[] = Array.from({ length: HOURS_IN_DAY }, (_, index) => ({
    index,
    startsAt: new Date(start + index * HOUR_MS).toISOString(),
    amount: new Decimal(0),
    count: 0,
  }))
  for (const item of items) {
    const index = Math.floor((Date.parse(item.hour) - start) / HOUR_MS)
    const bucket = buckets[index]
    if (!bucket) continue
    bucket.amount = bucket.amount.plus(item.amount.amount)
    bucket.count += item.count
  }
  return buckets
}

export function cumulativeAmounts(buckets: readonly HourBucket[]) {
  let total = new Decimal(0)
  return buckets.map((bucket) => {
    total = total.plus(bucket.amount)
    return total
  })
}

export function totalAmount(buckets: readonly HourBucket[]) {
  return buckets.reduce((sum, bucket) => sum.plus(bucket.amount), new Decimal(0))
}

export function totalCount(buckets: readonly HourBucket[]) {
  return buckets.reduce((sum, bucket) => sum + bucket.count, 0)
}

/** Hora corrente dentro do dia, de 0 a 23; fora do dia devolve o limite mais próximo. */
export function currentHourIndex(dayStart: string, now: Date) {
  const index = Math.floor((now.getTime() - Date.parse(dayStart)) / HOUR_MS)
  return Math.min(HOURS_IN_DAY - 1, Math.max(0, index))
}

/** Ontem até o mesmo minuto de hoje, com a hora corrente proporcional aos minutos passados. */
export function amountUntilSameTime(buckets: readonly HourBucket[], dayStart: string, now: Date) {
  const elapsed = (now.getTime() - Date.parse(dayStart)) / HOUR_MS
  if (elapsed <= 0) return new Decimal(0)
  if (elapsed >= HOURS_IN_DAY) return totalAmount(buckets)
  const fullHours = Math.floor(elapsed)
  const partial = buckets[fullHours]?.amount.times(elapsed - fullHours) ?? new Decimal(0)
  return totalAmount(buckets.slice(0, fullHours)).plus(partial)
}

export function peakBucket(buckets: readonly HourBucket[]) {
  let peak: HourBucket | null = null
  for (const bucket of buckets) {
    if (bucket.amount.lte(0)) continue
    if (!peak || bucket.amount.gt(peak.amount)) peak = bucket
  }
  return peak
}

export function percentChange(current: Decimal, previous: Decimal) {
  return previous.isZero() ? null : current.minus(previous).div(previous).times(100)
}

export function hourLabel(startsAt: string) {
  return `${String(new Date(startsAt).getHours()).padStart(2, '0')}h`
}

export function hourRangeLabel(startsAt: string) {
  const start = new Date(startsAt)
  const end = new Date(start.getTime() + HOUR_MS)
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${pad(start.getHours())}h às ${pad(end.getHours())}h`
}

/** Converte o recorte de hoje/ontem para o contrato do gráfico compartilhado. */
export function hourlyToSalesSeries(
  currency: string,
  todayStart: string,
  todayEnd: string,
  todayItems: readonly HourlyReceivablePoint[] = [],
  yesterdayItems: readonly HourlyReceivablePoint[] = [],
): AdminSalesSeries {
  const today = hourlyBuckets(todayStart, todayItems)
  const yesterday = hourlyBuckets(
    new Date(Date.parse(todayStart) - HOURS_IN_DAY * HOUR_MS).toISOString(),
    yesterdayItems,
  )
  return {
    granularity: 'hour',
    from: todayStart,
    to: todayEnd,
    series: [
      {
        currency,
        points: today.map((bucket, index) => ({
          bucket: bucket.startsAt,
          amount: { amount: bucket.amount.toFixed(3), currency },
          previousAmount: {
            amount: (yesterday[index]?.amount ?? new Decimal(0)).toFixed(3),
            currency,
          },
          count: bucket.count,
          previousCount: yesterday[index]?.count ?? 0,
        })),
      },
    ],
  }
}
